import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAllFootage, useTrips, isTrashed } from '../data/firestore';
import { bust } from '../utils/cacheBust';

// One browsable hub for every clip's multi-frame grade proofs. Proofs are photo
// assets tagged `clip-proof` with `proofOf == clipId`; we group them by clip so
// you can scan all 80+ clips at a glance instead of opening sessions one by one.
const proofImg = (p) => bust(p.derivatives?.magazine || p.displayUrl || p.thumbUrl || '', p);
const isBaking = (p) => p.stillGrab?.status === 'queued' || p.stillGrab?.status === 'rendering';
const frameOf = (p) => {
  const f = p.stillGrab?.frameIndex;
  return Number.isFinite(f) ? f : 0;
};

// Derive an HONEST state from real signals, not the session's default 'drafting'.
//   live=true  → something is actually rendering right now (animated badge)
//   otherwise  → a static status chip (no spinner, no "drafting" when idle)
function deriveState({ proofs, session }) {
  const bakingNow = proofs.filter(isBaking).length;
  const st = session?.status;
  if (bakingNow) return { key: 'baking', label: `Baking ${bakingNow} proof${bakingNow === 1 ? '' : 's'}`, live: true };
  if (st === 'authorized' || st === 'baking') return { key: 'baking', label: 'Baking full clip', live: true };
  if (st === 'done') return { key: 'done', label: 'Baked', live: false };
  if (!proofs.length) return { key: 'none', label: 'No proofs', live: false };
  if (session?.feedback?.length) return { key: 'review', label: 'In review', live: false };
  return { key: 'ready', label: 'Proofs ready', live: false };
}

function ProofThumb({ proof, onOpen }) {
  const img = proofImg(proof);
  const baking = isBaking(proof);
  return (
    <button type="button" className="cs-proofstrip-thumb" onClick={onOpen} title={`Frame ${proof.stillGrab?.frameIndex ?? '?'}`}>
      {img
        ? <img src={img} alt={`frame ${proof.stillGrab?.frameIndex ?? ''}`} style={{ opacity: baking ? 0.5 : 1 }} />
        : <div className="cs-empty" style={{ width: '100%', height: '100%' }} />}
      {baking && <span className="cs-proofstrip-baking">●</span>}
      {proof.override?.ops?.length ? <span className="cs-proofstrip-tag" title="This frame has per-frame adjustments on top of the auto grade">Tuned</span> : null}
      <span className="cs-proofstrip-frame">f{proof.stillGrab?.frameIndex ?? '?'}</span>
    </button>
  );
}

function ClipCard({ item, tripName, onOpen }) {
  const { proofs, name, session } = item;
  const state = deriveState(item);
  const updated = session?.updatedAt || Math.max(0, ...proofs.map((p) => p.stillGrab?.requeuedAt || 0));

  return (
    <div className="cs-proofcard">
      <div className="cs-proofcard-head" onClick={() => onOpen()}>
        <div style={{ minWidth: 0 }}>
          <div className="cs-proofcard-title" title={name}>{name}</div>
          <div className="cs-help" style={{ fontSize: 12, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {tripName && <span className="cs-card-tag">{tripName}</span>}
            <span>{proofs.length} proof{proofs.length === 1 ? '' : 's'}</span>
            {item.clip?.isRawVideo ? <span>· RED</span> : null}
            {updated ? <span>· {new Date(updated).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span> : null}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
          {state.live
            ? <span className="cs-grab-livetag">● {state.label}</span>
            : <span className={`cs-proof-status cs-proof-status-${state.key}`}>{state.label}</span>}
          <button className="cs-btn cs-btn-primary cs-btn-sm" onClick={(e) => { e.stopPropagation(); onOpen(); }}>
            {proofs.length ? 'Open session →' : 'Start session →'}
          </button>
        </div>
      </div>

      {proofs.length ? (
        <div className="cs-proofstrip">
          {proofs.map((p) => (
            <ProofThumb key={p.id} proof={p} onOpen={() => onOpen(p.id)} />
          ))}
        </div>
      ) : (
        <div className="cs-empty" style={{ margin: '6px 12px 12px' }}>No proofs yet — open to grab a few frames.</div>
      )}
    </div>
  );
}

// The body is embeddable — it renders as the "Clip grade sessions" tab of the
// consolidated /proofs page (ProofSheet.js) as well as standalone.
export function ClipProofsBody() {
  const navigate = useNavigate();
  const { docs: footage } = useAllFootage(6000);
  const { docs: trips } = useTrips();
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('updated');
  const [showEmpty, setShowEmpty] = useState(false);

  const tripName = useMemo(() => {
    const m = new Map((trips || []).map((t) => [t.id, t.name || t.id]));
    return (id) => m.get(id) || '';
  }, [trips]);

  const items = useMemo(() => {
    const all = (footage || []).filter((a) => !isTrashed(a));
    const clipsById = new Map();
    for (const a of all) if (a.kind === 'video') clipsById.set(a.id, a);

    const byClip = new Map(); // `${tripId}/${clipId}` -> proofs[]
    for (const a of all) {
      if (a.kind !== 'photo') continue;
      const isProof = a.proofOf || (Array.isArray(a.tags) && a.tags.includes('clip-proof'));
      if (!isProof) continue;
      const clipId = a.proofOf || a.lineage?.sourceAssetId || a.stillGrab?.sourceAssetId;
      if (!clipId) continue;
      const key = `${a.tripId}/${clipId}`;
      if (!byClip.has(key)) byClip.set(key, []);
      byClip.get(key).push(a);
    }

    const list = [];
    for (const [key, proofs] of byClip) {
      const [tripId, clipId] = key.split('/');
      const clip = clipsById.get(clipId) || null;
      proofs.sort((x, y) => frameOf(x) - frameOf(y));
      list.push({
        tripId, clipId, clip,
        name: clip?.name || proofs[0]?.lineage?.sourceName || 'Untitled clip',
        proofs,
        session: clip?.gradeSession || null,
      });
    }

    if (showEmpty) {
      for (const [clipId, clip] of clipsById) {
        const key = `${clip.tripId}/${clipId}`;
        if (byClip.has(key)) continue;
        list.push({ tripId: clip.tripId, clipId, clip, name: clip.name || 'Untitled clip', proofs: [], session: clip.gradeSession || null });
      }
    }
    return list;
  }, [footage, showEmpty]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = items;
    if (needle) list = list.filter((it) => `${it.name} ${tripName(it.tripId)}`.toLowerCase().includes(needle));
    const updatedOf = (it) => it.session?.updatedAt || Math.max(0, ...it.proofs.map((p) => p.stillGrab?.requeuedAt || 0));
    if (sort === 'updated') list = [...list].sort((a, b) => updatedOf(b) - updatedOf(a));
    else if (sort === 'proofs') list = [...list].sort((a, b) => b.proofs.length - a.proofs.length);
    else if (sort === 'name') list = [...list].sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [items, q, sort, tripName]);

  const totalProofs = items.reduce((n, it) => n + it.proofs.length, 0);
  const withProofs = items.filter((it) => it.proofs.length).length;

  const open = (it, proofId) => navigate(`/clip-grade/${it.tripId}/${it.clipId}${proofId ? `?proof=${proofId}` : ''}`);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
        <div>
          <div className="cs-help" style={{ fontSize: 13 }}>
            {withProofs} clip{withProofs === 1 ? '' : 's'} with proofs · {totalProofs} proof{totalProofs === 1 ? '' : 's'} total. Open a clip to review, give feedback, and authorize the full bake.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            className="cs-input cs-input-sm"
            placeholder="Search clips…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ minWidth: 180 }}
          />
          <select className="cs-input cs-input-sm" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort clips">
            <option value="updated">Recently updated</option>
            <option value="proofs">Most proofs</option>
            <option value="name">Name</option>
          </select>
          <label className="cs-aiasst-alignchk" title="Also list clips that have no proofs yet">
            <input type="checkbox" checked={showEmpty} onChange={(e) => setShowEmpty(e.target.checked)} /> Include clips w/o proofs
          </label>
        </div>
      </div>

      {!filtered.length ? (
        <div className="cs-empty">
          {footage == null ? <><span className="cs-spinner" /> Loading clips…</> : 'No clip proofs yet. Open a clip and grab a few frames to start a grade session.'}
        </div>
      ) : (
        <div className="cs-proofgrid">
          {filtered.map((it) => (
            <ClipCard key={`${it.tripId}/${it.clipId}`} item={it} tripName={tripName(it.tripId)} onOpen={(proofId) => open(it, proofId)} />
          )          )}
        </div>
      )}
    </div>
  );
}
